import React, { useEffect, useState } from 'react';
import { Route, Routes, useLocation } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { ErrorBoundary } from 'react-error-boundary';
import {
  AppShell,
  Burger,
  Flex,
  Group,
  Text,
  Title,
  useMantineTheme,
} from '@mantine/core';

import logo from '@assets/images/tc-gymnasium-logo.png';
import entityColor from '@util/entityColor';
import entityIcon from '@util/entityIcon';
import { Nameplate, Nav } from '@components/shared';
import appStore from '@duck/app-store';
import { AdminAthletes, AdminAthleteDetail, AdminTrainingBlockDetail, AdminWorkouts, AdminWorkoutDetail, AdminExercises, AdminExerciseDetail, AdminAchievements, AdminAchievementDetail, AdminGymClosures, AdminProfile } from '@pages';

import classes from './AdminApp.module.css';
import { RealtimeProvider } from '@lib/realtime';

export default function AdminApp() {
  const [opened, setOpened] = useState(false);
  const { pathname } = useLocation();
  const theme = useMantineTheme();
  const { admin } = appStore(
    (state) => ({
      admin: state.admin,
    }),
    shallow,
  );

  useEffect(() => {
    setOpened(false);
  }, [pathname]);

  const navLinks = [
    {
      text: 'Athletes',
      to: '/',
      icon: entityIcon.athlete(),
      color: entityColor.athlete,
    },
    {
      text: 'Workouts',
      to: '/workouts',
      icon: entityIcon.workout(),
      color: entityColor.workout,
    },
    {
      text: 'Exercises',
      to: '/exercises',
      icon: entityIcon.exercise(),
      color: entityColor.exercise,
    },
    {
      text: 'Achievements',
      to: '/achievements',
      icon: entityIcon.achievement(),
      color: entityColor.achievement,
    },
  ];

  return (
    <RealtimeProvider enabled>
      <AppShell
        padding="md"
        header={{ height: 65 }}
        navbar={{
          width: 250,
          breakpoint: 'sm',
          collapsed: { mobile: !opened },
        }}
      >
        <AppShell.Header
          pl="xs"
          pr="xs"
          style={{
            background: 'var(--brand-surface)',
            borderBottom: '1px solid var(--brand-sand)',
          }}
        >
          <Group
            justify="flex-end"
            align="center"
            style={{
              height: '100%',
            }}
          >
            <Flex align="center" gap="md" style={{ flexGrow: 1 }}>
              <div className={classes.burger}>
                <Burger
                  opened={opened}
                  onClick={() => setOpened(!opened)}
                  size="lg"
                  color={theme.colors.dark[1]}
                  mr="xl"
                />
              </div>
              <div className={classes.logo}>
                <img
                  src={logo}
                  alt="T&C's Gymnasium"
                  style={{
                    width: '52px',
                    height: '52px',
                    objectFit: 'contain',
                  }}
                />
              </div>
            </Flex>
            <div className={classes.right}>
              <Group gap="sm" align="center">
                <Nameplate title={admin?.full_name || 'Admin'} />
              </Group>
            </div>
          </Group>
        </AppShell.Header>
        <AppShell.Navbar pt="md">
          <Nav links={navLinks} />
          <div className={classes.nameplate}>
            <Group mb="sm">
              <Nameplate title={admin?.full_name || 'Admin'} />
            </Group>
          </div>
        </AppShell.Navbar>
        <AppShell.Main
          style={{
            background: 'var(--mantine-color-gray-0)',
          }}
        >
          <ErrorBoundary
            fallback={
              <div>
                <Title>That's not good!</Title>
                <Text>Something went wrong and we're going to fix it.</Text>
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<AdminAthletes />} />
              <Route path="/athletes/:athleteId" element={<AdminAthleteDetail />} />
              <Route
                path="/athletes/:athleteId/blocks/:blockId"
                element={<AdminTrainingBlockDetail />}
              />
              <Route path="/workouts" element={<AdminWorkouts />} />
              <Route path="/workouts/:workoutId" element={<AdminWorkoutDetail />} />
              <Route path="/exercises" element={<AdminExercises />} />
              <Route path="/exercises/:exerciseId" element={<AdminExerciseDetail />} />
              <Route path="/achievements" element={<AdminAchievements />} />
              <Route path="/achievements/:achievementId" element={<AdminAchievementDetail />} />
              <Route path="/gym-closures" element={<AdminGymClosures />} />
              <Route path="/profile" element={<AdminProfile />} />
              <Route path="*" element={<AdminAthletes />} />
            </Routes>
          </ErrorBoundary>
        </AppShell.Main>
      </AppShell>
    </RealtimeProvider>
  );
}
